import { useEffect, useState } from "react"

import "leaflet/dist/leaflet.css"
import "@/styles/leaflet-dark.css"
import "@/styles/leaflet-tooltip.css"

import type { MergedDeviceType } from "@/types"
import { Marker } from "@adamscybot/react-leaflet-component-marker"
import { Label } from "@radix-ui/react-dropdown-menu"
import { formatDistanceToNow } from "date-fns"
import L from "leaflet"
import {
  MapContainer,
  Polyline,
  Popup,
  TileLayer,
  Tooltip,
  useMap,
} from "react-leaflet"

import { ConsumerConnectionBillingStatusType } from "@/types/billing"
import { Icons } from "@/components/icons"

import { Card, CardTitle } from "../ui/card"
import { DropdownMenuSeparator } from "../ui/dropdown-menu"
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "../ui/sheet"
import { MapIcon } from "./map-icon"
import { TrackerIcon } from "./tracker-icon"

// This is the google maps API key. You can get it from the Google Cloud Console.
const GOOGLE_API_KEY = "AIzaSyAoJkDuvdUyHzqZxHnEIA_JjlnPBw-wCRw"
const MAP_IDS = {
  dark: "8914f89a8794e5e7",
  light: "d99756d81adee155",
}

const lightStyle: google.maps.MapTypeStyle[] = [
  {
    featureType: "all",
    elementType: "labels.icon",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    elementType: "geometry.fill",
    stylers: [
      {
        color: "#EAF7F7",
      },
    ],
  },
  {
    featureType: "administrative",
    elementType: "geometry.stroke",
    stylers: [
      {
        color: "#B8DEDC",
      },
    ],
  },
  {
    featureType: "administrative",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#3A6164",
      },
      {
        weight: 0.8,
      },
    ],
  },
  {
    featureType: "administrative",
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#FFFFFF",
      },
      {
        visibility: "on",
      },
      {
        weight: 3,
      },
    ],
  },
  {
    featureType: "landscape",
    elementType: "geometry.fill",
    stylers: [
      {
        color: "#F5FAFA",
      },
    ],
  },
  {
    featureType: "landscape.natural",
    elementType: "geometry.fill",
    stylers: [
      {
        color: "#E6F2F2",
      },
    ],
  },
  {
    featureType: "poi",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#6D8F8F",
      },
    ],
  },
  {
    featureType: "poi.park",
    elementType: "geometry.fill",
    stylers: [
      {
        color: "#C8E6C9",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "geometry.fill",
    stylers: [
      {
        color: "#D3E9E8",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "geometry.stroke",
    stylers: [
      {
        color: "#C8E4E3",
      },
      {
        weight: 0.4,
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#2E5A64",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#FFFFFF",
      },
      {
        visibility: "on",
      },
      {
        weight: 3,
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.fill",
    stylers: [
      {
        color: "#C0E1E0",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [
      {
        color: "#A5D2D0",
      },
      {
        weight: 0.6,
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#2E5A64",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#FFFFFF",
      },
      {
        visibility: "on",
      },
      {
        weight: 3,
      },
    ],
  },
  {
    featureType: "road.local",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#4C7373",
      },
    ],
  },
  {
    featureType: "road.local",
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#FFFFFF",
      },
      {
        visibility: "on",
      },
      {
        weight: 3,
      },
    ],
  },
  {
    featureType: "transit",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "water",
    elementType: "geometry.fill",
    stylers: [
      {
        color: "#D9F1F2",
      },
      {
        saturation: -10,
      },
      {
        lightness: 15,
      },
    ],
  },
]

const darkStyle: google.maps.MapTypeStyle[] = [
  {
    featureType: "all",
    elementType: "labels.icon",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    elementType: "geometry",
    stylers: [
      {
        color: "#1d2c4d",
      },
    ],
  },
  {
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#8ec3b9",
      },
    ],
  },
  {
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#1a3646",
      },
    ],
  },
  {
    featureType: "administrative.country",
    elementType: "geometry.stroke",
    stylers: [
      {
        color: "#4b6878",
      },
    ],
  },
  {
    featureType: "administrative.land_parcel",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#64779e",
      },
    ],
  },
  {
    featureType: "administrative.province",
    elementType: "geometry.stroke",
    stylers: [
      {
        color: "#4b6878",
      },
    ],
  },
  {
    featureType: "landscape.man_made",
    elementType: "geometry.stroke",
    stylers: [
      {
        color: "#334e87",
      },
    ],
  },
  {
    featureType: "landscape.natural",
    elementType: "geometry",
    stylers: [
      {
        color: "#023e58",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "geometry",
    stylers: [
      {
        color: "#283d6a",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "labels.text",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#6f9ba5",
      },
    ],
  },
  {
    featureType: "poi",
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#1d2c4d",
      },
    ],
  },
  {
    featureType: "poi.business",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "poi.park",
    elementType: "geometry.fill",
    stylers: [
      {
        color: "#023e58",
      },
    ],
  },
  {
    featureType: "poi.park",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#3C7680",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [
      {
        color: "#304a7d",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels.icon",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#98a5be",
      },
    ],
  },
  {
    featureType: "road",
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#1d2c4d",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "geometry",
    stylers: [
      {
        color: "#2c6675",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "geometry.stroke",
    stylers: [
      {
        color: "#255763",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#b0d5ce",
      },
    ],
  },
  {
    featureType: "road.highway",
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#023e58",
      },
    ],
  },
  {
    featureType: "transit",
    stylers: [
      {
        visibility: "off",
      },
    ],
  },
  {
    featureType: "transit",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#98a5be",
      },
    ],
  },
  {
    featureType: "transit",
    elementType: "labels.text.stroke",
    stylers: [
      {
        color: "#1d2c4d",
      },
    ],
  },
  {
    featureType: "transit.line",
    elementType: "geometry.fill",
    stylers: [
      {
        color: "#283d6a",
      },
    ],
  },
  {
    featureType: "transit.station",
    elementType: "geometry",
    stylers: [
      {
        color: "#3a4762",
      },
    ],
  },
  {
    featureType: "water",
    elementType: "geometry",
    stylers: [
      {
        color: "#0e1626",
      },
    ],
  },
  {
    featureType: "water",
    elementType: "labels.text.fill",
    stylers: [
      {
        color: "#4e6d70",
      },
    ],
  },
]

interface GoogleMapsLayerProps {
  theme?: "dark" | "light"
  mapType?: "roadmap" | "satellite" | "hybrid" | "terrain"
}

function GoogleMapsLayer({ theme = "dark", mapType }: GoogleMapsLayerProps) {
  const map = useMap()

  useEffect(() => {
    let googleLayer: L.GridLayer
    let script: HTMLScriptElement | null = null

    const initMap = () => {
      try {
        const container = map.getContainer()
        container.classList.remove("light-theme", "dark-theme")
        container.classList.add(`${theme}-theme`)

        googleLayer = L.gridLayer.googleMutant({
          type: mapType,
          maxZoom: 22,
          ...(mapType === "roadmap"
            ? {
                styles: theme === "dark" ? darkStyle : lightStyle,
                mapOptions: {
                  mapId: theme === "dark" ? MAP_IDS.dark : MAP_IDS.light,
                  disableDefaultUI: true,
                  styles: theme === "dark" ? darkStyle : lightStyle,
                },
              }
            : {}),
        })
        googleLayer.addTo(map)

        console.log("DEBUG>>>", googleLayer)
      } catch (err) {
        console.error("Google Maps initialization failed:", err)
      }
    }

    if (!window.google) {
      script = document.createElement("script")
      script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=places`
      script.async = true
      script.defer = true
      script.onerror = () => console.error("Failed to load Google Maps script")

      const checkLoaded = () => {
        if (window.google) {
          initMap()
        } else {
          setTimeout(checkLoaded, 100)
        }
      }

      script.onload = checkLoaded
      document.head.appendChild(script)
    } else {
      initMap()
    }

    return () => {
      if (googleLayer) map.removeLayer(googleLayer)
      if (script) document.head.removeChild(script)
      const container = map.getContainer()
      container.classList.remove("light-theme", "dark-theme")
    }
  }, [map, mapType, theme])

  return null
}

interface MapUpdaterProps {
  position: [number, number]
}

function MapUpdater({ position }: MapUpdaterProps) {
  const map = useMap()

  useEffect(() => {
    map.setView(position, map.getZoom() || 14)
  }, [map, position])

  return null
}

interface MapProps {
  position: [number, number]

  markers?: ConsumerConnectionBillingStatusType[]
  trackMarkers?: MergedDeviceType[]
  theme?: "light" | "dark"
  mapType?: "roadmap" | "satellite" | "hybrid" | "terrain"
}

export function Map({
  position,
  markers,
  trackMarkers,
  theme = "light",
  mapType,
}: MapProps) {
  const [route, setRoute] = useState<[number, number][]>([])
  const [openSheet, setOpenSheet] = useState<string | null>(null)

  return (
    <div className={`map-container ${theme}-theme`}>
      <MapContainer
        className="h-screen w-full"
        center={position}
        zoom={14}
        minZoom={1}
        maxZoom={18}
        scrollWheelZoom={true}
        doubleClickZoom={false}
        attributionControl={false}
      >
        <GoogleMapsLayer theme={theme} mapType={mapType} />
        <MapUpdater position={position} />

        {markers?.map((marker) => (
          <Marker
            key={`marker-${marker.consumer_id}`}
            position={[marker.lat, marker.lon]}
            icon={<MapIcon connectionStatus={marker.connection_status} />}
            eventHandlers={{
              click: () => setOpenSheet(marker.consumer_id),
            }}
          />
        ))}

        {trackMarkers?.map((marker) => (
          <Marker
            key={`tracker-${marker.device_id}`}
            position={[marker.latitude, marker.longitude]}
            icon={<TrackerIcon device={marker} selected={false} />}
          >
            <Tooltip direction="right" opacity={1} permanent>
              <div className="flex flex-col gap-1 rounded-lg border bg-background p-2 text-foreground">
                <div className="font-semibold">{marker.full_name}</div>
                <div className="flex capitalize">
                  <Icons.creditCard className="mr-2 h-4 w-4" />
                  {marker.device_id}
                </div>
                <div className="flex capitalize">
                  <Icons.clock className="mr-2 h-4 w-4" />
                  {formatDistanceToNow(new Date(marker.timestamp), {
                    addSuffix: true,
                  })}
                </div>
              </div>
            </Tooltip>
          </Marker>
        ))}

        {route.length > 0 && (
          <Polyline positions={route} color="blue" weight={4} opacity={0.7} />
        )}
      </MapContainer>

      {markers?.map((marker) => (
        <Sheet
          key={`sheet-${marker.consumer_id}`}
          open={openSheet === marker.consumer_id}
          onOpenChange={(open) => !open && setOpenSheet(null)}
        >
          <SheetContent className="flex flex-col items-center justify-around p-0">
            <div className="mt-16 flex flex-col items-center p-8">
              <Icons.circleUser className="h-24 w-24" />
              <p className="mt-2 text-2xl font-semibold text-primary">
                {marker.consumer_name}
              </p>
            </div>
            <Card className="h-screen w-full rounded-b-none rounded-t-[50px] p-8">
              <div>
                <CardTitle className="mb-4 mt-10 text-primary">
                  Meter Information
                </CardTitle>
                <DropdownMenuSeparator />
                <div className="mt-4 flex flex-col gap-2">
                  <span className="flex items-center gap-2">
                    <Icons.circleGauge className="mr-2 h-4 w-4" />
                    <p>160868650</p>
                  </span>
                </div>
                <CardTitle className="mb-4 mt-10 text-primary">
                  Status
                </CardTitle>
                <DropdownMenuSeparator />
                <div className="mt-4 flex flex-col gap-2">
                  <span className="flex items-center gap-2">
                    <Icons.user className="mr-2 h-4 w-4" />
                    {marker.connection_status}
                  </span>
                  <span className="flex items-center gap-2">
                    <Icons.creditCard className="mr-2 h-4 w-4" />
                    {marker.billing_status.replace("_", " ")}
                  </span>
                </div>
                <CardTitle className="mb-4 mt-10 text-primary">
                  Location Information
                </CardTitle>
                <DropdownMenuSeparator />
                <div className="mt-4 flex flex-col gap-2">
                  <span className="flex items-center gap-2">
                    <Icons.mapPin className="mr-2 h-4 w-4" />
                    {`Coordinates: ${marker.lat} - ${marker.lon}`}
                  </span>
                </div>
              </div>
            </Card>
          </SheetContent>
        </Sheet>
      ))}
    </div>
  )
}
